import { PhotoStatus } from '@/lib/constants/photo_status';

export interface Location {
  cityname: string;
  stateabbr: string;
  latitude: number;
  longitude: number;
  zipcode: string;
}

export interface AccountData {
  ssoUUID: string;
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  originalPhoto: string;
  croppedPhoto: string;
  location: Location;
  roles: string[];
  hasPremiumHistory: boolean;
  photoStatus: PhotoStatus | string;
  createdAt?: string;
  careType?: string;
}

export enum AccountRoles {
  FamilyConcierge = 'com.sittercity.empire:Role.family_concierge',
  Sitter = 'com.sittercity.empire:Role.provider',
  Parent = 'com.sittercity.empire:Role.seeker',
  Premium = 'com.sittercity.empire:Role.premium_seeker',
  Corporate = 'com.sittercity.empire:Role.corporate_seeker',
  FeaturedSitter = 'com.sittercity.empire:Role.featured_provider',
}

export enum AccountTypes {
  FamilyConcierge = 'family_concierge',
  Sitter = 'sitter',
  Parent = 'basic',
  Premium = 'premium',
  Corporate = 'corporate',
  FeaturedSitter = 'featured_sitter',
}

export const RoleMap = {} as Map<AccountRoles, AccountTypes>;
RoleMap[AccountRoles.FamilyConcierge] = AccountTypes.FamilyConcierge;
RoleMap[AccountRoles.Corporate] = AccountTypes.Corporate;
RoleMap[AccountRoles.Premium] = AccountTypes.Premium;
RoleMap[AccountRoles.Parent] = AccountTypes.Parent;
RoleMap[AccountRoles.FeaturedSitter] = AccountTypes.FeaturedSitter;
RoleMap[AccountRoles.Sitter] = AccountTypes.Sitter;

export function accountType(roles: Array<AccountRoles>): AccountTypes | null {
  // ensure the correct order of precedence
  const found = [
    AccountRoles.FamilyConcierge,
    AccountRoles.Corporate,
    AccountRoles.Premium,
    AccountRoles.Parent,
    AccountRoles.FeaturedSitter,
    AccountRoles.Sitter,
  ].find((role) => roles.includes(role));

  if (!found) return null;
  return RoleMap[found];
}

export class Account implements AccountData {
  static accountType(): import('vue/types/options').PropValidator<unknown> {
    throw new Error('Method not implemented.');
  }

  ssoUUID!: string;

  id!: string;

  name!: string;

  firstName!: string;

  lastName!: string;

  email!: string;

  originalPhoto!: string;

  croppedPhoto!: string;

  location!: Location;

  hasPremiumHistory!: boolean;

  photoStatus!: PhotoStatus | string;

  roles!: string[];

  createdAt?: string;

  careType?: string;

  constructor(data: AccountData) {
    Object.assign(this, data);
  }

  isParent(): boolean {
    const parentRoles = [AccountRoles.Parent, AccountRoles.Premium, AccountRoles.Corporate];
    return !!this.roles.find((r: string) => parentRoles.includes(r as AccountRoles));
  }

  isPremium(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Premium as string));
  }

  isCorporate(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Corporate as string));
  }

  isSitter(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.Sitter as string));
  }

  isFeaturedSitter(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.FeaturedSitter as string));
  }

  isFamilyConcierge(): boolean {
    return !!this.roles.find((r: string) => r === (AccountRoles.FamilyConcierge as string));
  }

  isBasic(): boolean {
    return !(this.isPremium() || this.isCorporate());
  }

  accountType(): AccountTypes | null {
    return accountType(this.roles as Array<AccountRoles>);
  }

  accountKind(): string {
    if (this.isSitter()) {
      return 'SITTER';
    }
    if (this.isParent()) {
      return 'PARENT';
    }
    return 'UNKNOWN';
  }
}
